import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAnalyticsFilters } from "../filters-context";
import { toInputDate, fromInputDate } from "../utils";
import type { AnalyticsFiltersState } from "../types";

export function FiltersToolbar() {
  const {
    state,
    setFilter,
    resetFilters,
    options,
    derivedOptions,
    loading,
    error,
    lockedFilters,
  } = useAnalyticsFilters();

  const isLocked = React.useCallback(
    (key: keyof AnalyticsFiltersState) =>
      Object.prototype.hasOwnProperty.call(lockedFilters, key) &&
      lockedFilters[key] !== undefined,
    [lockedFilters],
  );

  const onDateChange = (field: "from" | "to") => (value: string) => {
    const next = {
      ...state.dateRange,
      [field]: fromInputDate(value),
    };
    setFilter("dateRange", next);
  };

  const renderSelect = (
    label: string,
    items: Array<{ id: number; nombre: string }>,
    value: number | null,
    onValueChange: (id: number | null) => void,
    placeholder: string,
    disabled?: boolean,
    locked?: boolean,
  ) =>
    locked ? null : (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        <Select
          value={value ? String(value) : "all"}
          onValueChange={(next) =>
            onValueChange(next === "all" ? null : Number(next))
          }
          disabled={disabled || locked}
        >
          <SelectTrigger className="min-w-[180px]">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {items.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );

  const filtersDisabled = loading || !!error;

  return (
    <div className="flex flex-col sm:flex-row lg:flex-wrap gap-3 sm:gap-4">
      {renderSelect(
        "Manager",
        options?.clientesIntermedios ?? [],
        state.clienteIntermedioId,
        (id) => setFilter("clienteIntermedioId", id),
        "Selecciona un manager",
        filtersDisabled,
        isLocked("clienteIntermedioId"),
      )}
      {renderSelect(
        "Cliente",
        derivedOptions.clientesFinales,
        state.clienteFinalId,
        (id) => setFilter("clienteFinalId", id),
        "Selecciona un cliente",
        filtersDisabled || derivedOptions.clientesFinales.length === 0,
        isLocked("clienteFinalId"),
      )}
      {renderSelect(
        "Programa",
        derivedOptions.programas,
        state.programaId,
        (id) => setFilter("programaId", id),
        "Selecciona un programa",
        filtersDisabled || derivedOptions.programas.length === 0,
        isLocked("programaId"),
      )}
      {renderSelect(
        "Actividad",
        derivedOptions.actividades,
        state.actividadId,
        (id) => setFilter("actividadId", id),
        "Selecciona una actividad",
        filtersDisabled || derivedOptions.actividades.length === 0,
        isLocked("actividadId"),
      )}

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          Fecha desde
        </span>
        <Input
          type="date"
          value={toInputDate(state.dateRange.from)}
          onChange={(e) => onDateChange("from")(e.target.value)}
          className="w-full sm:min-w-[170px] h-9 sm:h-10 text-sm"
          disabled={filtersDisabled}
        />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
          Fecha hasta
        </span>
        <Input
          type="date"
          value={toInputDate(state.dateRange.to)}
          onChange={(e) => onDateChange("to")(e.target.value)}
          className="w-full sm:min-w-[170px] h-9 sm:h-10 text-sm"
          disabled={filtersDisabled}
        />
      </div>

      <div className="flex items-center gap-2 pt-6 sm:pt-0">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={resetFilters}
          disabled={filtersDisabled}
          className="text-xs sm:text-sm h-8 sm:h-9"
        >
          Limpiar filtros
        </Button>
        {error && (
          <span className="text-xs sm:text-sm text-red-600">{error}</span>
        )}
      </div>
    </div>
  );
}
